import config from "../../../config/config";
import { authFetch, buildAuthHeaders, openScreenFetch } from "../../../public/api/httpClient";
import { EMPTY_MY_TOURNAMENTS_OVERVIEW } from "../utils/myTournamentsConstants";

const TOURNAMENT_STUDENT_BASE = `${config.BASE_URL}/tournament-student`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function parseContentDispositionFilename(header) {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export class StudentTournamentCertificateError extends Error {
  constructor(message, code = "") {
    super(message);
    this.name = "StudentTournamentCertificateError";
    this.code = code;
  }
}

/**
 * Read-only overview from tournament registrations (optional auth).
 */
export const getMyTournamentsOverview = async () => {
  const res = await openScreenFetch(
    `${TOURNAMENT_STUDENT_BASE}/my-tournaments-overview`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    }
  );
  const data = await parseJson(res);
  if (res.status === 403 && data.code === "STUDENT_ROLE_REQUIRED") {
    const err = new Error(data.message || "Student login required");
    err.code = "STUDENT_ROLE_REQUIRED";
    throw err;
  }
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch tournament overview");
  }

  return {
    ...data,
    data: data?.data || EMPTY_MY_TOURNAMENTS_OVERVIEW,
  };
};

/** Download PDF certificate for one tournament registration (logged-in student). */
export const downloadMyTournamentCertificate = async (tournamentDbId, csrfToken) => {
  const res = await authFetch(`${TOURNAMENT_STUDENT_BASE}/certificates/download`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(
      csrfToken
        ? { "X-CSRF-Token": csrfToken, Accept: "application/pdf" }
        : { Accept: "application/pdf" }
    ),
    body: JSON.stringify({ tournament_id: Number(tournamentDbId) }),
  });

  const contentType = res.headers.get("Content-Type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const data = await parseJson(res);
      throw new StudentTournamentCertificateError(
        data.message || "No certificate available",
        data.code || ""
      );
    }
    const text = await res.text();
    throw new StudentTournamentCertificateError(text || "Certificate download failed");
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ||
    "tournament-certificate.pdf";
  triggerBrowserDownload(blob, filename);
  return { filename };
};
